import {
  CustomTemplateListRequest,
  CustomTemplateListResponse,
  GetItemsResponse,
  ReadCustomTemplateListResponse,
  UpdateIntegrationResponse
} from "../../../ui/gzCustomTemplateList/types";
import {ConsoleRequest, DataProviderType} from "../../../utils/request";
import {BaseWebComponentDataProvider} from "../../../framework/BaseWebComponentDataProvider";
import {IntegrationActionContext, IntegrationInstancesMethods} from "../types";
import {IntegrationsMarketplaceCardActionButton} from "../../gzIntegrationsMarketplaceCard/types";
import {ButtonColors, ButtonTypes} from "../../../ui/gzButton/types";
import {computeRequestForReadIntegrations, populateData} from "../../common/utils";
import {GRID_TYPES, INTEGRATION_TYPES, LICENSE_STATUSES_INSTALLED} from "../../common/constants";
import {translateFn} from "../../../utils/translator.helper";
import {Dependencies} from "../../integrationsMarketplaceAppContainer/types/dependencies";

export class IntegrationsMarketplaceGridInstalledNirvanaDataProvider extends BaseWebComponentDataProvider {
  private cache: CustomTemplateListResponse | null;
  private endpoint = 'IntegrationInstances';


  constructor(public dependencies: Dependencies) {
    super();
  }

  async getItems(request: CustomTemplateListRequest): Promise<GetItemsResponse> {
    const newRequest = computeRequestForReadIntegrations(request);
    const response = await this.readRecords(newRequest);

    const data = this.computeItemsData(response);

    return {
      itemsTotal: response.total,
      items: data,
    };
  }

  async deleteIntegration(payload: any): Promise<UpdateIntegrationResponse> {
    const response = await this.deleteRecord(payload);

    return {
      success: response.success,
    };
  }

  async disableIntegration(payload: any): Promise<UpdateIntegrationResponse> {
    const response = await this.disableRecord(payload);

    return {
      success: response.success,
    };
  }

  async enableIntegration(payload: any): Promise<UpdateIntegrationResponse> {
    const response = await this.enableRecord(payload);

    return {
      success: response.success,
    };
  }

  private computeItemsData(response: ReadCustomTemplateListResponse): any {
    const data = populateData(response.data, GRID_TYPES.INSTALLED, this.dependencies.permissions);
    // Add action buttons dynamically based on status
    data.forEach((item: any) => {
      item.actionButtons = this.getActionButtons(item);
    });

    return data;
  }

  private getActionButtons(item: any): IntegrationsMarketplaceCardActionButton[] {
    const buttons: IntegrationsMarketplaceCardActionButton[] = [];

    switch (item.type) {
      case INTEGRATION_TYPES.INTEGRATION_TYPE_VMWARE:
        if (item.statusType === LICENSE_STATUSES_INSTALLED.ACTIVE
          || item.statusType === LICENSE_STATUSES_INSTALLED.PENDING) {
          buttons.push({
            label: translateFn('Stop'),
            type: ButtonTypes.OUTLINE,
            color: ButtonColors.SECONDARY,
            action: IntegrationActionContext.Stop,
            disabled: !this.dependencies.permissions.canAddEditIntegrations || !item.licensed,
          });
        } else {
          buttons.push({
            label: translateFn('Start'),
            type: ButtonTypes.OUTLINE,
            color: ButtonColors.PRIMARY,
            action: IntegrationActionContext.Start, // Assuming 'Start' is the correct action here
            disabled: item.statusType === LICENSE_STATUSES_INSTALLED.ACTION_REQUIRED
              || !this.dependencies.permissions.canAddEditIntegrations
              || !item.licensed,
          });
        }
        break;
    }

    buttons.push({
      label: translateFn('Delete'),
      type: ButtonTypes.FULL,
      color: ButtonColors.NEGATIVE,
      action: IntegrationActionContext.Delete,
      disabled: !this.dependencies.permissions.canAddEditIntegrations,
    });

    return buttons;
  }


  private async readRecords(request): Promise<ReadCustomTemplateListResponse> {
    const payload = ConsoleRequest.mapGridRequestToPayload(request, this.dependencies.getTimezone());

    payload.extraParams = request.extraParams || {};

    // payload.extraParams.companyId = this.dependencies.getActiveCompany().companyId;

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      payload,
      endpoint: this.endpoint,
      method: IntegrationInstancesMethods.READ_RECORDS,
    });

    return response;
  }

  private async deleteRecord(recordId: string): Promise<UpdateIntegrationResponse> {
    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: this.endpoint,
      method: IntegrationInstancesMethods.DELETE_RECORDS,
      payload: {
        records: recordId,
      },
    });

    return response;
  }


  private async disableRecord(payload: any): Promise<UpdateIntegrationResponse> {
    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: this.endpoint,
      method: IntegrationInstancesMethods.MANAGE_RECORD,
      payload: {
        integrationId: payload.integrationId,
        method: IntegrationActionContext.Stop,
        extraParams: {},
      },
    });

    return response;
  }

  private async enableRecord(payload: any): Promise<UpdateIntegrationResponse> {
    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: this.endpoint,
      method: IntegrationInstancesMethods.MANAGE_RECORD,
      payload: {
        integrationId: payload.integrationId,
        method: IntegrationActionContext.Start,
        extraParams: {},
      },
    });

    return response;
  }
}
